import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Search, Eye, Mail, Send, MessageSquare } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { CommunicationHistory } from '../../components/CommunicationHistory';

const API = '/api';

export const AdminInquiriesPage = () => {
  const { t } = useLanguage();
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  
  // Reply dialog state
  const [replyDialogOpen, setReplyDialogOpen] = useState(false);
  const [replyMessage, setReplyMessage] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchInquiries();
  }, [statusFilter]);

  const fetchInquiries = async () => {
    try {
      let url = `${API}/admin/inquiries`;
      if (statusFilter !== 'all') url += `?status=${statusFilter}`;
      
      const response = await fetch(url, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setInquiries(data);
      }
    } catch (error) {
      console.error('Error fetching inquiries:', error);
      toast.error('Failed to load inquiries');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (inquiryId, status) => {
    try {
      const response = await fetch(`${API}/admin/inquiries/${inquiryId}/status?status=${status}`, {
        method: 'PUT',
        credentials: 'include',
      });

      if (response.ok) {
        toast.success('Status updated');
        fetchInquiries();
        if (selectedInquiry?.inquiry_id === inquiryId) {
          setSelectedInquiry(prev => ({ ...prev, status }));
        }
      } else {
        toast.error('Failed to update status');
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleReply = async () => {
    if (!replyMessage.trim()) {
      toast.error('Please enter a reply message');
      return;
    }
    
    setSending(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/admin/inquiries/${selectedInquiry.inquiry_id}/reply`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ reply_message: replyMessage }),
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(result.email_status === 'mocked' ? 'Reply logged (test mode)' : 'Reply sent successfully');
        setReplyDialogOpen(false);
        setReplyMessage('');
        fetchInquiries();
        // Update selected inquiry status
        setSelectedInquiry(prev => ({ ...prev, status: 'responded' }));
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to send reply');
      }
    } catch (error) {
      toast.error('Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  const filteredInquiries = inquiries.filter(inq => 
    inq.name.toLowerCase().includes(search.toLowerCase()) ||
    inq.email.toLowerCase().includes(search.toLowerCase()) ||
    inq.subject.toLowerCase().includes(search.toLowerCase())
  );

  const getStatusColor = (status) => {
    const colors = {
      new: 'bg-blue-100 text-blue-800',
      read: 'bg-yellow-100 text-yellow-800',
      responded: 'bg-green-100 text-green-800',
      closed: 'bg-gray-100 text-gray-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const statuses = ['new', 'read', 'responded', 'closed'];

  return (
    <div className="p-6" data-testid="admin-inquiries-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-[#111827]">{t('admin.inquiries')}</h1>
        <p className="text-[#6B7280]">Manage contact form inquiries</p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6B7280]" />
              <Input
                placeholder={t('admin.search')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="ps-9"
                data-testid="search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {statuses.map(s => (
                  <SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#33AADD] mx-auto"></div>
            </div>
          ) : filteredInquiries.length === 0 ? (
            <div className="text-center py-8 text-[#6B7280]">{t('admin.noData')}</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Subject</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>{t('admin.status')}</TableHead>
                    <TableHead>{t('admin.date')}</TableHead>
                    <TableHead className="text-end">{t('admin.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredInquiries.map((inq, index) => (
                    <TableRow key={inq.inquiry_id} data-testid={`inquiry-row-${index}`}>
                      <TableCell>
                        <div>
                          <p className="font-medium text-[#111827]">{inq.name}</p>
                          <p className="text-sm text-[#6B7280]">{inq.email}</p>
                        </div>
                      </TableCell>
                      <TableCell className="max-w-[200px] truncate">{inq.subject}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{inq.inquiry_type}</Badge>
                      </TableCell>
                      <TableCell>
                        <Select value={inq.status} onValueChange={(v) => updateStatus(inq.inquiry_id, v)}>
                          <SelectTrigger className="w-28 h-8">
                            <Badge className={getStatusColor(inq.status)}>{inq.status}</Badge>
                          </SelectTrigger>
                          <SelectContent>
                            {statuses.map(s => (
                              <SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="text-sm text-[#6B7280]">
                        {inq.created_at ? format(new Date(inq.created_at), 'MMM d, yyyy') : '-'}
                      </TableCell>
                      <TableCell className="text-end">
                        <Button variant="ghost" size="sm" onClick={() => setSelectedInquiry(inq)}>
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Inquiry Details Dialog */}
      <Dialog open={!!selectedInquiry} onOpenChange={() => setSelectedInquiry(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Inquiry Details</DialogTitle>
          </DialogHeader>
          {selectedInquiry && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-[#6B7280]">Name</p>
                  <p className="font-medium">{selectedInquiry.name}</p>
                </div>
                <div>
                  <p className="text-sm text-[#6B7280]">Email</p>
                  <p className="font-medium">{selectedInquiry.email}</p>
                </div>
                {selectedInquiry.phone && (
                  <div>
                    <p className="text-sm text-[#6B7280]">Phone</p>
                    <p className="font-medium">{selectedInquiry.phone}</p>
                  </div>
                )}
                <div>
                  <p className="text-sm text-[#6B7280]">Type</p>
                  <Badge variant="outline">{selectedInquiry.inquiry_type}</Badge>
                </div>
              </div>

              <div>
                <p className="text-sm text-[#6B7280] mb-1">Subject</p>
                <p className="font-medium">{selectedInquiry.subject}</p>
              </div>

              <div>
                <p className="text-sm text-[#6B7280] mb-2">Message</p>
                <p className="text-sm whitespace-pre-line bg-[#F9FAFB] p-4 rounded-lg">{selectedInquiry.message}</p>
              </div>

              {/* Reply Action */}
              <div className="p-4 border rounded-lg bg-gradient-to-r from-blue-50 to-cyan-50">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-[#111827] flex items-center gap-2">
                    <MessageSquare className="h-4 w-4 text-blue-600" />
                    Communication
                  </h4>
                </div>
                <Button
                  onClick={() => setReplyDialogOpen(true)}
                  data-testid="reply-inquiry-btn"
                >
                  <Mail className="h-4 w-4 mr-2" />
                  Reply to Inquiry
                </Button>
              </div>

              {/* Communication History */}
              <CommunicationHistory
                recordType="inquiry"
                recordId={selectedInquiry.inquiry_id}
                title="Communication History"
                maxHeight="200px"
              />

              <div className="border-t pt-4">
                <p className="text-sm text-[#6B7280] mb-2">Update Status</p>
                <Select value={selectedInquiry.status} onValueChange={(v) => updateStatus(selectedInquiry.inquiry_id, v)}>
                  <SelectTrigger className="w-48">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {statuses.map(s => (
                      <SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reply Dialog */}
      <Dialog open={replyDialogOpen} onOpenChange={setReplyDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Reply to Inquiry
            </DialogTitle>
          </DialogHeader>
          {selectedInquiry && (
            <div className="space-y-4">
              <div className="bg-gray-50 p-3 rounded-lg text-sm">
                <p><strong>To:</strong> {selectedInquiry.name} &lt;{selectedInquiry.email}&gt;</p>
                <p><strong>Re:</strong> {selectedInquiry.subject}</p>
              </div>
              
              <div>
                <Label htmlFor="replyMessage">Your Reply</Label>
                <Textarea
                  id="replyMessage"
                  value={replyMessage}
                  onChange={(e) => setReplyMessage(e.target.value)}
                  placeholder="Type your reply here..."
                  rows={6}
                />
              </div>
              
              <p className="text-xs text-blue-600 bg-blue-50 p-2 rounded">
                Email is currently in test mode. Messages are logged but not actually sent until email service is activated.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setReplyDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleReply} disabled={sending}>
              {sending ? 'Sending...' : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Send Reply
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminInquiriesPage;
